import { useState } from 'react'
import { X, RotateCcw } from 'lucide-react'

interface FilterProps {
  industry: string
  businessType: string
  scoreRange: { min: number; max: number }
  riskLevel: string
  location: string
  registrationDateRange: { start: string; end: string }
}

interface BusinessSearchFiltersProps {
  filters: FilterProps
  onFiltersChange: (filters: FilterProps) => void
}

const industries = [
  'Technology',
  'Manufacturing',
  'Healthcare',
  'Finance',
  'Retail',
  'Education',
  'Real Estate',
  'Agriculture',
  'Transportation',
  'Energy',
  'Other'
]

const businessTypes = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'llp', label: 'Limited Liability Partnership' }
]

const riskLevels = [
  { value: 'low', label: 'Low Risk', color: 'text-green-600' },
  { value: 'medium', label: 'Medium Risk', color: 'text-yellow-600' },
  { value: 'high', label: 'High Risk', color: 'text-orange-600' },
  { value: 'critical', label: 'Critical Risk', color: 'text-red-600' }
]

const states = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Puducherry'
]

export function BusinessSearchFilters({ filters, onFiltersChange }: BusinessSearchFiltersProps) {
  const [localFilters, setLocalFilters] = useState(filters)

  const handleFilterChange = (key: keyof FilterProps, value: any) => {
    const newFilters = { ...localFilters, [key]: value }
    setLocalFilters(newFilters)
    onFiltersChange(newFilters)
  }

  const handleScoreRangeChange = (type: 'min' | 'max', value: number) => {
    const newRange = { ...localFilters.scoreRange, [type]: value }
    handleFilterChange('scoreRange', newRange)
  }

  const handleDateRangeChange = (type: 'start' | 'end', value: string) => {
    const newRange = { ...localFilters.registrationDateRange, [type]: value }
    handleFilterChange('registrationDateRange', newRange)
  }

  const resetFilters = () => {
    const defaultFilters: FilterProps = {
      industry: '',
      businessType: '',
      scoreRange: { min: 0, max: 100 },
      riskLevel: '',
      location: '',
      registrationDateRange: { start: '', end: '' }
    }
    setLocalFilters(defaultFilters)
    onFiltersChange(defaultFilters)
  }

  const hasActiveFilters = () => {
    return (
      localFilters.industry ||
      localFilters.businessType ||
      localFilters.riskLevel ||
      localFilters.location ||
      localFilters.scoreRange.min > 0 ||
      localFilters.scoreRange.max < 100 ||
      localFilters.registrationDateRange.start ||
      localFilters.registrationDateRange.end
    )
  }

  return (
    <div className="space-y-6">
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Search Filters
        </h3>
        {hasActiveFilters() && (
          <button
            onClick={resetFilters}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset All
          </button>
        )}
      </div>

      {/* Filter Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Industry Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Industry
          </label>
          <select
            value={localFilters.industry}
            onChange={(e) => handleFilterChange('industry', e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Industries</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
        </div>

        {/* Business Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Business Type
          </label>
          <select
            value={localFilters.businessType}
            onChange={(e) => handleFilterChange('businessType', e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            {businessTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Risk Level Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Risk Level
          </label>
          <select
            value={localFilters.riskLevel}
            onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Risk Levels</option>
            {riskLevels.map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        {/* Location Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            State/Location
          </label>
          <select
            value={localFilters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Locations</option>
            {states.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>

        {/* Score Range Filter */}
        <div className="md:col-span-2 lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            TLBH Score Range
          </label>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="0"
                max="100"
                value={localFilters.scoreRange.min}
                onChange={(e) => handleScoreRangeChange('min', parseInt(e.target.value) || 0)}
                className="w-20 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Min"
              />
              <span className="text-gray-500">to</span>
              <input
                type="number"
                min="0"
                max="100"
                value={localFilters.scoreRange.max}
                onChange={(e) => handleScoreRangeChange('max', parseInt(e.target.value) || 100)}
                className="w-20 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Max"
              />
            </div>
            <div className="relative">
              <input
                type="range"
                min="0"
                max="100"
                value={localFilters.scoreRange.min}
                onChange={(e) => handleScoreRangeChange('min', parseInt(e.target.value))}
                className="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <input
                type="range"
                min="0"
                max="100"
                value={localFilters.scoreRange.max}
                onChange={(e) => handleScoreRangeChange('max', parseInt(e.target.value))}
                className="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>0</span>
              <span>50</span>
              <span>100</span>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Date Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Registration Date Range
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              From
            </label>
            <input
              type="date"
              value={localFilters.registrationDateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              To
            </label>
            <input
              type="date"
              value={localFilters.registrationDateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex flex-wrap gap-2">
            {localFilters.industry && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                Industry: {localFilters.industry}
                <button
                  onClick={() => handleFilterChange('industry', '')}
                  className="ml-2 text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {localFilters.businessType && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                Type: {businessTypes.find(t => t.value === localFilters.businessType)?.label}
                <button
                  onClick={() => handleFilterChange('businessType', '')}
                  className="ml-2 text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {localFilters.riskLevel && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                Risk: {riskLevels.find(r => r.value === localFilters.riskLevel)?.label}
                <button
                  onClick={() => handleFilterChange('riskLevel', '')}
                  className="ml-2 text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {(localFilters.scoreRange.min > 0 || localFilters.scoreRange.max < 100) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                Score: {localFilters.scoreRange.min}-{localFilters.scoreRange.max}
                <button
                  onClick={() => handleFilterChange('scoreRange', { min: 0, max: 100 })}
                  className="ml-2 text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}